package de.xtl.retroguide.protocol

import kotlinx.serialization.Serializable

const val MAX_DISPLAY_NAME_LENGTH = 40
const val MAX_PARTICIPANT_NAMES = 30
const val MAX_ANSWER_LENGTH = 1_000
const val MAX_ANSWERS_PER_QUESTION = 20
const val MAX_QUESTION_LENGTH = 240
const val MAX_QUESTIONS = 10
const val MIN_QUESTION_DURATION_SECONDS = 1
const val MAX_QUESTION_DURATION_SECONDS = 60 * 60
const val DEFAULT_QUESTION_DURATION_SECONDS = 5 * 60
const val SESSION_CODE_LENGTH = 10
const val MAX_CLUSTERS = 20
const val MAX_CLUSTER_NAME_LENGTH = 60
const val MAX_CLUSTER_ID_LENGTH = 64
const val DEFAULT_VOTES_PER_PARTICIPANT = 3
const val MAX_VOTES_PER_PARTICIPANT = 10

@Serializable
data class CreateSessionRequest(val displayName: String)

@Serializable
data class JoinSessionRequest(val displayName: String)

@Serializable
data class UpdateParticipantNamesRequest(val names: List<String>)

@Serializable
data class StartRetroQuestion(
    val text: String,
    val durationSeconds: Int? = null,
    val widget: QuestionWidget = QuestionWidget.TEXT,
    val showAllParticipantAnswers: Boolean = false,
    /** Index into the submitted question list; only used by [QuestionWidget.DOT_VOTING]. */
    val sourceQuestionIndex: Int? = null,
    val votesPerParticipant: Int = DEFAULT_VOTES_PER_PARTICIPANT,
)

@Serializable
data class StartRetroRequest(val questions: List<StartRetroQuestion>)

@Serializable
data class StartQuestionTimerRequest(val durationSeconds: Int)

@Serializable
data class SetQuestionAnswerVisibilityRequest(val showAllParticipantAnswers: Boolean)

@Serializable
data class SubmitAnswerRequest(val content: String)

@Serializable
data class AnswerCluster(
    val id: String,
    val name: String,
    val answerIds: List<String>,
)

@Serializable
data class UpdateClustersRequest(val clusters: List<AnswerCluster>)

@Serializable
data class VotingOption(
    val id: String,
    val label: String,
    val category: AnswerCategory? = null,
    val memberCount: Int = 1,
)

@Serializable
data class SetAnswerChoiceRequest(val choice: ParticipantAnswerChoice?)

@Serializable
enum class ParticipantAnswerChoice {
    NO_ANSWER,
    NO_MORE_ANSWERS,
}

@Serializable
enum class ParticipantRole {
    MODERATOR,
    PARTICIPANT,
}

@Serializable
enum class QuestionWidget {
    TEXT,
    BATTERY,
    WEATHER,
    SAILBOAT,
    MAD_SAD_GLAD,
    STARFISH,
    FOUR_L,
    ROTI,
    HOT_AIR_BALLOON,
    SPEEDCAR,
    KALM,
    DAKI,
    TEAM_RADAR,
    DOT_VOTING,
    ACTION_ITEMS,
}

@Serializable
enum class BatteryLevel(val percent: Int) {
    LOW(25),
    MEDIUM(50),
    HIGH(100),
}

@Serializable
enum class WeatherMood {
    SUNNY,
    PARTLY_CLOUDY,
    CLOUDY,
    RAINY,
    STORMY,
}

@Serializable
data class SessionAccess(
    val sessionCode: String,
    val participantId: String,
    val accessToken: String,
    val role: ParticipantRole,
    val title: String,
)

@Serializable
data class ParticipantNameOption(
    val name: String,
    val isSelected: Boolean,
)

@Serializable
data class SessionRoster(
    val sessionCode: String,
    val names: List<ParticipantNameOption>,
    val isEnded: Boolean = false,
)

@Serializable
data class LobbyParticipant(
    val id: String,
    val displayName: String,
    val role: ParticipantRole,
)

@Serializable
enum class RetroPhase {
    LOBBY,
    QUESTION_ACTIVE,
    ANSWERS_REVEALED,
    FINISHED,
}

@Serializable
data class RetroQuestion(
    val id: String,
    val text: String,
    val durationSeconds: Int? = null,
    val widget: QuestionWidget = QuestionWidget.TEXT,
    val showAllParticipantAnswers: Boolean = false,
    val sourceQuestionId: String? = null,
    val votesPerParticipant: Int = DEFAULT_VOTES_PER_PARTICIPANT,
)

@Serializable
data class VisibleAnswer(
    val id: String,
    val participantName: String,
    val content: String,
)

@Serializable
data class VisibleAnswerChoice(
    val participantName: String,
    val choice: ParticipantAnswerChoice,
)

@Serializable
data class QuestionSummary(
    val question: RetroQuestion,
    val answers: List<VisibleAnswer>,
    val answerChoices: List<VisibleAnswerChoice> = emptyList(),
    val clusters: List<AnswerCluster> = emptyList(),
    val votingOptions: List<VotingOption> = emptyList(),
)

@Serializable
data class SessionSnapshot(
    val sessionCode: String,
    val title: String,
    val participants: List<LobbyParticipant>,
    val participantNames: List<String> = emptyList(),
    val phase: RetroPhase,
    val currentQuestion: RetroQuestion? = null,
    val questionIndex: Int? = null,
    val questionCount: Int = 0,
    val answerCount: Int = 0,
    val submittedParticipantIds: List<String> = emptyList(),
    val answerCountsByParticipantId: Map<String, Int> = emptyMap(),
    val answerChoicesByParticipantId: Map<String, ParticipantAnswerChoice> = emptyMap(),
    val ownAnswerChoice: ParticipantAnswerChoice? = null,
    val ownAnswers: List<VisibleAnswer> = emptyList(),
    val visibleAnswers: List<VisibleAnswer> = emptyList(),
    val visibleAnswerChoices: List<VisibleAnswerChoice> = emptyList(),
    val questionSummaries: List<QuestionSummary> = emptyList(),
    val questionRemainingSeconds: Int? = null,
    val isAnsweringAllowed: Boolean = false,
    val isEnded: Boolean = false,
    val clusters: List<AnswerCluster> = emptyList(),
    val votingOptions: List<VotingOption> = emptyList(),
)
